/**
 * AdminTeachersMockedRail — the three design right-rail modules whose data
 * has no source yet: Next Payday (payroll hero), Hiring Pipeline
 * (recruitment stages), Commission Payouts (scheduled payout rows). Each
 * renders the design layout as an em-dash skeleton behind its own
 * <MockedOverlay>, cited to BON-156 (payroll / recruitment / commission
 * sources not yet wired).
 */
import { MockedOverlay } from '../primitives';

const EM_DASH = '—';
const ROWS = ['a', 'b', 'c', 'd'] as const;

const heading: React.CSSProperties = { fontSize: 13, letterSpacing: '0.04em', margin: 0 };
const subtle = 'var(--nb-subtle)';

const PIPELINE_STAGES: ReadonlyArray<{ label: string; color: string }> = [
  { label: 'Applications', color: '#748074' },
  { label: 'Phone screen', color: '#257bea' },
  { label: 'Trial workshop', color: '#BA7517' },
  { label: 'Offer extended', color: '#0e874b' },
];

function NextPaydayCard() {
  return (
    <MockedOverlay reason="Next-payday date + period cost needs the payroll source. Tracked in BON-156.">
      <section className="nb-card">
        <div className="nb-card-hd">
          <h3 className="nb-display" style={heading}>
            Next Payday
          </h3>
          <span className="nb-eyebrow">{EM_DASH}</span>
        </div>
        <div
          style={{
            padding: 14,
            background: 'var(--nb-cream)',
            borderRadius: 8,
            border: '1px solid var(--nb-border)',
            marginBottom: 10,
          }}
        >
          <div className="nb-display" style={{ fontSize: 18, color: 'var(--nb-forest)' }}>
            {EM_DASH}
          </div>
          <div className="nb-tabular" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
            {EM_DASH}
          </div>
          <div style={{ fontSize: 11, color: 'var(--nb-muted)', marginTop: 2 }}>
            {EM_DASH} people · pay period
          </div>
        </div>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: subtle }}
        >
          <span>
            Pending PTO <strong style={{ color: 'var(--nb-ink)' }}>{EM_DASH}</strong>
          </span>
          <span>
            Bonuses <strong style={{ color: 'var(--nb-ink)' }}>{EM_DASH}</strong>
          </span>
        </div>
      </section>
    </MockedOverlay>
  );
}

function HiringPipelineCard() {
  return (
    <MockedOverlay reason="Hiring pipeline stages need a recruitment source. Tracked in BON-156.">
      <section className="nb-card">
        <div className="nb-card-hd">
          <h3 className="nb-display" style={heading}>
            Hiring Pipeline
          </h3>
          <span className="nb-eyebrow">national</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {PIPELINE_STAGES.map(({ label, color }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <i style={{ width: 8, height: 8, borderRadius: 999, background: color }} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: subtle }}>{label}</span>
              <span className="nb-tabular nb-mono" style={{ fontSize: 13, color: subtle }}>
                {EM_DASH}
              </span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--nb-border)' }}>
          <div className="nb-eyebrow" style={{ marginBottom: 6 }}>
            Top regions hiring
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {[EM_DASH, EM_DASH, EM_DASH].map((_, i) => (
              <span key={i} className="nb-pill" style={{ fontSize: 10.5, color: subtle }}>
                {EM_DASH}
              </span>
            ))}
          </div>
        </div>
      </section>
    </MockedOverlay>
  );
}

function CommissionPayoutsCard() {
  return (
    <MockedOverlay reason="Scheduled commission payouts need the payroll source. Tracked in BON-156.">
      <section className="nb-card">
        <div className="nb-card-hd">
          <h3 className="nb-display" style={heading}>
            Commission Payouts
          </h3>
          <span className="nb-eyebrow">scheduled</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ROWS.map((k) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span
                className="nb-pill nb-status-soft"
                style={{ height: 18, fontSize: 9.5, padding: '0 6px', color: subtle }}
              >
                {EM_DASH}
              </span>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: subtle }}>
                {EM_DASH}
              </span>
              <span className="nb-tabular nb-mono" style={{ fontSize: 13, color: subtle }}>
                {EM_DASH}
              </span>
            </div>
          ))}
        </div>
      </section>
    </MockedOverlay>
  );
}

export function AdminTeachersMockedRail() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0, minHeight: 0 }}>
      <NextPaydayCard />
      <HiringPipelineCard />
      <CommissionPayoutsCard />
    </div>
  );
}
